import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Avatar,
    TextField,
    Select,
    MenuItem,
    Button,
    Box,
    Typography,
    CircularProgress,
    FormControl,
    InputLabel,
    SelectChangeEvent,
} from "@mui/material";
import {
    Close as CloseIcon,
    PhotoOutlined,
    VideoCallOutlined,
    AttachFileOutlined,
    Person as PersonIcon, // Default icon if profile photo is not available
} from "@mui/icons-material";
import styles from "./CreatePostModal.module.scss";
import FileUploadModal from "../../FileUploadModal/FileUploadModal"; // Adjusted path
import {
    PostCategory,
    POST_CATEGORIES,
    CATEGORY_DESCRIPTIONS,
} from "../../../constants/categories"; // Adjusted path
import { containsBadWords } from "../../../utils/contentFilter"; // Added import
import { MAX_POST_IMAGES } from "../../../constants/appConstants";
import LoadingOverlay from "../../ui/loading/LoadingOverlay";

// Props definition
export interface CreatePostModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: { id?: string; firstName?: string; lastName?: string } | null;
    userProfilePhoto: string | null;
    onAddPost: (data: {
        postText: string;
        media: Array<{ mediaType: string; mediaUrl: string; thumbnailUrl?: string }>;
        category: PostCategory;
    }) => Promise<void>;
    isPostingInProgress: boolean;
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({
    isOpen,
    onClose,
    user,
    userProfilePhoto,
    onAddPost,
    isPostingInProgress,
}) => {
    // Internal State
    const [postText, setPostText] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<PostCategory>("General");
    const [currentUploadedMedia, setCurrentUploadedMedia] = useState<
        Array<{
            mediaType: string;
            mediaUrl: string;
            thumbnailUrl?: string;
            fileName?: string;
            fileSize?: number;
        }>
    >([]);
    const [postTextError, setPostTextError] = useState(false);
    const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
    const [selectedFileTypeForUpload, setSelectedFileTypeForUpload] = useState("");
    const [contentError, setContentError] = useState<string | null>(null);
    const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

    // Determine if the image upload limit has been reached
    const isMediaLimitReached = currentUploadedMedia.length >= MAX_POST_IMAGES;

    // Reset state when modal is closed or reopened
    useEffect(() => {
        if (isOpen) {
            setPostText("");
            setSelectedCategory("General");
            setCurrentUploadedMedia([]);
            setPostTextError(false);
            setContentError(null);
            setShowSuccessOverlay(false);
        } else {
            setIsFileUploadModalOpen(false);
        }
    }, [isOpen]);

    // Handle the transition from posting to success
    useEffect(() => {
        // Only set showSuccessOverlay when transitioning from posting to not posting
        if (!isPostingInProgress && showSuccessOverlay) {
            // The success overlay will handle its own timing and cleanup
            return;
        }
        setShowSuccessOverlay(isPostingInProgress);
    }, [isPostingInProgress]);

    const handlePostTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPostText(event.target.value);
        if (postTextError && event.target.value.trim() !== "") {
            setPostTextError(false);
        }
        if (contentError && !containsBadWords(event.target.value)) {
            // Clear content error if text is changed and no longer has bad words
            setContentError(null);
        }
    };

    const handleCategoryChange = (event: SelectChangeEvent<PostCategory>) => {
        setSelectedCategory(event.target.value as PostCategory);
    };

    const handleOpenFileUploadModal = (fileType: string) => {
        setSelectedFileTypeForUpload(fileType);
        setIsFileUploadModalOpen(true);
    };

    const handleCloseFileUploadModal = () => {
        setIsFileUploadModalOpen(false);
        setSelectedFileTypeForUpload("");
    };

    const handleFilesUploadedCallback = useCallback(
        (filesData: Array<{ mediaType: string; mediaUrl: string; thumbnailUrl?: string }>) => {
            // Append the new files to the existing ones
            setCurrentUploadedMedia((prevMedia) => [...prevMedia, ...filesData]);
        },
        [],
    );

    const handleRemoveMediaItem = (indexToRemove: number) => {
        setCurrentUploadedMedia((prevMedia) =>
            prevMedia.filter((_, index) => index !== indexToRemove),
        );
    };

    const handleSubmitPost = async () => {
        if (postText.trim() === "") {
            setPostTextError(true);
            setContentError(null); // Clear any previous content error
            return;
        }
        setPostTextError(false);

        // Check for bad words
        if (containsBadWords(postText)) {
            setContentError("Your post contains inappropriate language. Please revise it.");
            // alert("Your post contains inappropriate language. Please revise it."); // Replaced alert with state for better UX
            return;
        }
        setContentError(null); // Clear error if passes

        try {
            await onAddPost({
                postText,
                media: currentUploadedMedia,
                category: selectedCategory,
            });
            // Parent (Newsfeed.tsx) will handle closing the modal via 'onClose' after successful post
            // and resetting its own isPosting state.
            // Resetting local form state is handled by useEffect when isOpen changes or upon successful submission by parent.
        } catch (error) {
            console.error("[CreatePostModal] Error submitting post:", error);
            // Error handling (e.g., showing a snackbar) could be managed here or by the parent
        }
    };

    const effectiveUserProfilePhoto = userProfilePhoto || undefined;
    // const userName = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "User";

    return (
        <Dialog
            open={isOpen}
            onClose={onClose} // Allow closing by clicking backdrop or pressing Esc
            aria-labelledby="create-post-dialog-title"
            PaperProps={{
                className: styles.modalDialogPaper,
            }}
            maxWidth="sm" // 'sm' is a good default, can be overridden by styles.modalDialogPaper
            fullWidth
        >
            {(isPostingInProgress || (showSuccessOverlay && !isPostingInProgress)) && (
                <div className={styles.loadingOverlay}>
                    <LoadingOverlay
                        isLoading={isPostingInProgress}
                        message="Creating your post..."
                        success={!isPostingInProgress && showSuccessOverlay}
                        successMessage="Post created successfully! 🎉"
                    />
                </div>
            )}
            <DialogTitle className={styles.dialogTitle} id="create-post-dialog-title">
                Create Post
                <IconButton
                    aria-label="close"
                    className={styles.closeButton}
                    onClick={onClose}
                    disabled={isPostingInProgress}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={styles.dialogContent}>
                {/* <Box className={styles.userInfo}>
                    <Avatar src={effectiveUserProfilePhoto}>
                        {!effectiveUserProfilePhoto && <PersonIcon />}
                    </Avatar>
                    <Typography variant="subtitle1" className={styles.userName}>
                        {userName}
                    </Typography>
                </Box> */}

                <TextField
                    className={styles.postTextArea} // General class for potential global overrides
                    multiline
                    rows={5}
                    placeholder={`What's on your mind?`}
                    fullWidth
                    variant="outlined"
                    value={postText}
                    onChange={handlePostTextChange}
                    error={postTextError || !!contentError} // Combine errors for TextField
                    helperText={
                        postTextError
                            ? "Please write something to post."
                            : contentError
                              ? contentError
                              : ""
                    } // Show appropriate error
                    disabled={isPostingInProgress}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "var(--background-color-lighter)", // Slightly different background for text area
                            borderRadius: "8px",
                            "& fieldset": {
                                borderColor: postTextError ? "red" : "var(--border-color)",
                            },
                            "&:hover fieldset": {
                                borderColor: postTextError ? "red" : "var(--primary-color)",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: postTextError ? "red" : "var(--primary-color)",
                                borderWidth: postTextError ? "1px" : "2px",
                            },
                        },
                        "& .MuiInputBase-input::placeholder": {
                            color: "var(--text-secondary)",
                            opacity: 1,
                        },
                    }}
                />

                <FormControl
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={isPostingInProgress}
                >
                    <InputLabel id="post-category-select-label">Category</InputLabel>
                    <Select
                        labelId="post-category-select-label"
                        id="post-category-select"
                        value={selectedCategory}
                        label="Choose a Tag"
                        onChange={handleCategoryChange}
                        sx={{
                            borderRadius: "8px",
                            backgroundColor: "var(--background-color-lighter)",
                        }}
                    >
                        {POST_CATEGORIES.filter((cat) => cat !== "All").map((category) => (
                            <MenuItem key={category} value={category}>
                                {category}
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                    - {CATEGORY_DESCRIPTIONS[category]}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {currentUploadedMedia.length > 0 && (
                    <Box className={styles.mediaPreviewContainer}>
                        {currentUploadedMedia.map((media, index) => (
                            <Box key={index} className={styles.mediaPreviewItem}>
                                {/* Enhanced media preview for different file types */}
                                {(media.mediaType === "image" || media.mediaType === "gif") && (
                                    <img
                                        src={media.mediaUrl}
                                        alt={`preview ${index}`}
                                        style={{
                                            width: "80px",
                                            height: "80px",
                                            objectFit: "cover",
                                            borderRadius: "4px",
                                        }}
                                    />
                                )}
                                {media.mediaType === "video" && (
                                    <video
                                        src={media.mediaUrl}
                                        controls
                                        style={{
                                            width: "80px",
                                            height: "80px",
                                            objectFit: "cover",
                                            borderRadius: "4px",
                                        }}
                                    />
                                )}
                                {media.mediaType === "pdf" && (
                                    <Box
                                        sx={{
                                            padding: "0.5rem",
                                            textAlign: "center",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "80px",
                                            width: "80px",
                                        }}
                                    >
                                        <AttachFileOutlined
                                            sx={{ fontSize: "2rem", color: "red" }}
                                        />
                                        <Typography variant="caption" display="block" noWrap>
                                            PDF
                                        </Typography>
                                    </Box>
                                )}
                                {(media.mediaType === "document" || media.mediaType === "file") && (
                                    <Box
                                        sx={{
                                            padding: "0.5rem",
                                            textAlign: "center",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "80px",
                                            width: "80px",
                                        }}
                                    >
                                        <AttachFileOutlined
                                            sx={{ fontSize: "2rem", color: "blue" }}
                                        />
                                        <Typography variant="caption" display="block" noWrap>
                                            {media.fileName || "Document"}
                                        </Typography>
                                    </Box>
                                )}
                                <IconButton
                                    size="small"
                                    className={styles.removeMediaButton}
                                    onClick={() => handleRemoveMediaItem(index)}
                                    disabled={isPostingInProgress}
                                    aria-label={`remove media ${index + 1}`}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}

                <Box className={styles.attachmentBar}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                        Add to your post:
                    </Typography>
                    <IconButton
                        className={styles.attachmentButton}
                        onClick={() => handleOpenFileUploadModal("image")}
                        disabled={isPostingInProgress || isMediaLimitReached}
                        aria-label="add photo"
                    >
                        <PhotoOutlined sx={{ color: "var(--icon-color-photo)" }} />
                        {!isPostingInProgress && (
                            <Typography
                                variant="caption"
                                sx={{ display: { xs: "none", sm: "block" } }}
                            >
                                Photo
                            </Typography>
                        )}
                    </IconButton>
                    {/* Enhanced media upload buttons for video, PDF, and documents */}
                    <IconButton
                        className={styles.attachmentButton}
                        onClick={() => handleOpenFileUploadModal("video")}
                        disabled={isPostingInProgress || isMediaLimitReached}
                        aria-label="add video"
                    >
                        <VideoCallOutlined sx={{ color: "var(--icon-color-video)" }} />
                        {!isPostingInProgress && (
                            <Typography
                                variant="caption"
                                sx={{ display: { xs: "none", sm: "block" } }}
                            >
                                Video
                            </Typography>
                        )}
                    </IconButton>
                    <IconButton
                        className={styles.attachmentButton}
                        onClick={() => handleOpenFileUploadModal("pdf")}
                        disabled={isPostingInProgress || isMediaLimitReached}
                        aria-label="add PDF"
                    >
                        <AttachFileOutlined sx={{ color: "var(--icon-color-pdf)" }} />
                        {!isPostingInProgress && (
                            <Typography
                                variant="caption"
                                sx={{ display: { xs: "none", sm: "block" } }}
                            >
                                PDF
                            </Typography>
                        )}
                    </IconButton>
                    <IconButton
                        className={styles.attachmentButton}
                        onClick={() => handleOpenFileUploadModal("document")}
                        disabled={isPostingInProgress || isMediaLimitReached}
                        aria-label="add document"
                    >
                        <AttachFileOutlined sx={{ color: "var(--icon-color-file)" }} />
                        {!isPostingInProgress && (
                            <Typography
                                variant="caption"
                                sx={{ display: { xs: "none", sm: "block" } }}
                            >
                                Document
                            </Typography>
                        )}
                    </IconButton>
                </Box>
                {isMediaLimitReached && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, textAlign: "center" }}>
                        You can add a maximum of {MAX_POST_IMAGES} photos.
                    </Typography>
                )}
            </DialogContent>

            <DialogActions className={styles.dialogActions}>
                <Button
                    onClick={handleSubmitPost}
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={postText.trim() === "" || isPostingInProgress}
                    startIcon={
                        isPostingInProgress ? <CircularProgress size={20} color="inherit" /> : null
                    }
                    sx={{ borderRadius: "20px", padding: "0.5rem 1.5rem", fontWeight: "bold" }}
                >
                    {isPostingInProgress ? "Posting..." : "Post"}
                </Button>
            </DialogActions>

            <FileUploadModal
                isVisible={isFileUploadModalOpen}
                onClose={handleCloseFileUploadModal}
                fileType={selectedFileTypeForUpload}
                onFilesUploaded={handleFilesUploadedCallback} // Pass the updated callback
            />
        </Dialog>
    );
};

export default CreatePostModal;
